import React from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Container,
    Divider,
    Grid,
    Stack,
    Typography,
    Link as MuiLink,
} from "@mui/material";
import { ArrowForward as ArrowForwardIcon } from "@mui/icons-material";
import { Link as RouterLink, useNavigate } from "react-router-dom";

import {
    BILLING_CYCLES,
    formatCurrency,
    getCycleLabel,
    getCycleSuffix,
    getDisplayPrice,
    getNetPriceFor,
    monthlyPriceForTier,
    PLAN_DEFINITIONS,
    PLAN_TIERS,
    VAT_RATE,
    yearlyPriceForTier,
} from "../../subscription";
import RegistrationFields from "./registration/RegistrationFields";
import RegistrationPlanSection from "./registration/RegistrationPlanSection";
import RegistrationSidebar from "./registration/RegistrationSidebar";
import useRegistrationForm from "./registration/useRegistrationForm";
import { uniqueFeatures, withVat } from "./registration/registrationUtils";

export default function Registration() {
    const navigate = useNavigate();
    const {
        formData,
        showInclVat,
        setShowInclVat,
        showPw,
        setShowPw,
        showPw2,
        setShowPw2,
        loading,
        error,
        fieldErrors,
        adminRegistrationAvailable,
        publicUserRegistrationEnabled,
        isTestPlan,
        showTestPlanOption,
        tier,
        cycle,
        handleChange,
        handleAdminToggle,
        handleTierSelect,
        handleCycleSelect,
        handlePaidUntilChange,
        handleSubmit,
    } = useRegistrationForm({ navigate });

    const vatPercent = Math.round(VAT_RATE * 100);
    const vatLabel = showInclVat ? `inkl. USt (${vatPercent} %)` : `zzgl. USt (${vatPercent} %)`;

    const tierDefinition = PLAN_DEFINITIONS[tier];
    const basePlanFeatures = PLAN_DEFINITIONS[PLAN_TIERS.INVOICE].features;
    const accountingFeatures = React.useMemo(
        () => uniqueFeatures([...basePlanFeatures, ...PLAN_DEFINITIONS[PLAN_TIERS.ACCOUNTING].features]),
        [basePlanFeatures]
    );

    const featuresToDisplay = React.useMemo(
        () => uniqueFeatures(
            tier === PLAN_TIERS.ACCOUNTING
                ? [...basePlanFeatures, ...PLAN_DEFINITIONS[PLAN_TIERS.ACCOUNTING].features]
                : basePlanFeatures
        ),
        [tier, basePlanFeatures]
    );

    const monthlyPrice = isTestPlan ? 0 : monthlyPriceForTier(tier, showInclVat);
    const yearlyPrice = isTestPlan ? 0 : yearlyPriceForTier(tier, showInclVat);
    const currentPrice = isTestPlan ? 0 : getDisplayPrice(formData.paymentPlan, showInclVat);
    const priceSuffix = isTestPlan ? "individuell" : getCycleSuffix(cycle);
    const cycleLabel = isTestPlan ? "individuell" : getCycleLabel(cycle);
    const formattedPrice = formatCurrency(currentPrice);

    const monthlyNet = isTestPlan ? 0 : getNetPriceFor(tier, BILLING_CYCLES.MONTHLY);
    const yearlyNet = isTestPlan ? 0 : getNetPriceFor(tier, BILLING_CYCLES.YEARLY);
    const yearlySavingNet = monthlyNet * 12 - yearlyNet;
    const yearlySavingDisplay = !isTestPlan && yearlySavingNet > 0 ? formatCurrency(withVat(yearlySavingNet, showInclVat)) : null;
    const yearlyEffective = !isTestPlan && yearlyNet > 0 ? formatCurrency(withVat(yearlyNet / 12, showInclVat)) : null;

    if (!publicUserRegistrationEnabled) {
        return (
            <Box sx={{ bgcolor: "background.default", minHeight: "100vh", display: "flex", alignItems: "center", py: 6 }}>
                <Container maxWidth="md">
                    <Card sx={{ borderRadius: 3, boxShadow: "0 6px 24px rgba(0,0,0,0.06)" }}>
                        <CardHeader
                                title="Registrierung über Keycloak"
                                subheader="Die öffentliche Registrierung läuft in diesem Modus nicht mehr direkt über Schreiberlein."
                        />
                        <CardContent>
                            <Stack spacing={2}>
                                <Typography>
                                    Lege dein Konto zuerst in Keycloak an und melde dich danach in Schreiberlein an.
                                    Beim ersten erfolgreichen Login wird dein lokaler Fachbenutzer automatisch angelegt
                                    und du wirst anschließend durch den Tarif- und Bezahlprozess geführt.
                                </Typography>
                                <Stack direction={{ xs: "column", sm: "row" }} gap={1}>
                                    <Button component={RouterLink} to="/login" variant="contained" sx={{ bgcolor: "orange", "&:hover": { bgcolor: "#cc7a00" } }}>
                                        Zu Keycloak / Login
                                    </Button>
                                    <Button component={RouterLink} to="/" variant="outlined">
                                        Zur Startseite
                                    </Button>
                                </Stack>
                            </Stack>
                        </CardContent>
                    </Card>
                </Container>
            </Box>
        );
    }

    return (
        <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
            <Box
                sx={{
                    position: "relative",
                    py: { xs: 6, md: 8 },
                    background:
                        "radial-gradient(1100px 500px at 10% -10%, rgba(255,165,0,0.12), transparent), radial-gradient(700px 400px at 100% 0%, rgba(255,165,0,0.10), transparent)",
                }}
            >
                <Container maxWidth={false} sx={{ px: { xs: 2, sm: 3, md: 6 } }}>
                    <Grid container spacing={3} alignItems="stretch">
                        <Grid item xs={12} md={7}>
                            <Card
                                sx={{
                                    height: "100%",
                                    borderRadius: 3,
                                    boxShadow: "0 6px 24px rgba(0,0,0,0.06)",
                                }}
                                component="form"
                                onSubmit={handleSubmit}
                            >
                                <CardHeader
                                    title="Registrierung"
                                    subheader="In wenigen Schritten zu deinem Schreiberlein-Konto"
                                />
                                <CardContent>
                                    <Grid container spacing={2}>
                                        <RegistrationFields
                                            formData={formData}
                                            fieldErrors={fieldErrors}
                                            showPw={showPw}
                                            showPw2={showPw2}
                                            onChange={handleChange}
                                            onToggleShowPw={() => setShowPw((value) => !value)}
                                            onToggleShowPw2={() => setShowPw2((value) => !value)}
                                        />

                                        <RegistrationPlanSection
                                            showInclVat={showInclVat}
                                            onToggleVat={setShowInclVat}
                                            adminRegistrationAvailable={adminRegistrationAvailable}
                                            registerAsAdmin={formData.registerAsAdmin}
                                            onAdminToggle={handleAdminToggle}
                                            tier={tier}
                                            showTestPlanOption={showTestPlanOption}
                                            basePlanFeatures={basePlanFeatures}
                                            accountingFeatures={accountingFeatures}
                                            onTierSelect={handleTierSelect}
                                            isTestPlan={isTestPlan}
                                            paidUntil={formData.paidUntil}
                                            paidUntilError={fieldErrors.paidUntil}
                                            onPaidUntilChange={handlePaidUntilChange}
                                            cycle={cycle}
                                            onCycleSelect={handleCycleSelect}
                                            monthlyPrice={monthlyPrice}
                                            yearlyPrice={yearlyPrice}
                                            vatLabel={vatLabel}
                                            yearlySavingDisplay={yearlySavingDisplay}
                                            yearlyEffective={yearlyEffective}
                                        />
                                    </Grid>

                                    {error && (
                                        <Typography color="error" sx={{ mt: 1 }}>
                                            {error}
                                        </Typography>
                                    )}

                                    <Divider sx={{ my: 2 }} />
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Mit deiner Registrierung akzeptierst du unsere{" "}
                                        <MuiLink component={RouterLink} to="/terms" underline="hover">
                                            AGB
                                        </MuiLink>
                                        ,{" "}
                                        <MuiLink component={RouterLink} to="/privacy" underline="hover">
                                            Datenschutzbestimmungen
                                        </MuiLink>{" "}
                                        und bestätigst das{" "}
                                        <MuiLink component={RouterLink} to="/imprint" underline="hover">
                                            Impressum
                                        </MuiLink>
                                        .
                                    </Typography>

                                    <Stack direction={{ xs: "column", sm: "row" }} gap={1}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            disabled={loading}
                                            endIcon={<ArrowForwardIcon />}
                                            sx={{
                                                bgcolor: "orange",
                                                "&:hover": { bgcolor: "#cc7a00" },
                                                borderRadius: 2,
                                            }}
                                        >
                                            {loading ? "Wird erstellt…" : "Konto erstellen"}
                                        </Button>
                                        <Button component={RouterLink} to="/login" variant="outlined" sx={{ borderRadius: 2 }}>
                                            Ich habe bereits ein Konto
                                        </Button>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={5}>
                            <RegistrationSidebar
                                tierDefinition={tierDefinition}
                                formattedPrice={formattedPrice}
                                priceSuffix={priceSuffix}
                                vatLabel={vatLabel}
                                cycle={cycle}
                                cycleLabel={cycleLabel}
                                yearlySavingDisplay={yearlySavingDisplay}
                                yearlyEffective={yearlyEffective}
                                featuresToDisplay={featuresToDisplay}
                            />
                        </Grid>
                    </Grid>
                </Container>
            </Box>
        </Box>
    );
}
